import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Clock3,
  Minus,
  PackageOpen,
  ReceiptText,
} from 'lucide-react';
import { RestaurantDashboard } from '../../../types/restaurant/RestaurantDashboardTypes';
import {
  formatDashboardDate,
  formatDashboardMoney,
  formatDashboardTime,
} from './restaurantDashboardFormatters';

const RESTAURANT_ROUTES = {
  orders: '/restaurant/operation/orders',
};

export const DashboardPanel = ({
  title,
  subtitle,
  action,
  children,
  className = '',
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <section
    className={`rounded-xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark ${className}`}
  >
    <header className="flex items-start justify-between gap-4 border-b border-stroke px-5 py-4 dark:border-strokedark">
      <div>
        <h2 className="font-semibold text-black dark:text-white">{title}</h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-bodydark2">{subtitle}</p>
        )}
      </div>
      {action}
    </header>
    {children}
  </section>
);

export const KpiCard = ({
  label,
  value,
  detail,
  icon,
  tone = 'primary',
  href,
}: {
  label: string;
  value: string;
  detail: ReactNode;
  icon: ReactNode;
  tone?: 'primary' | 'success' | 'warning' | 'danger';
  href?: string;
}) => {
  const navigate = useNavigate();

  const tones = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/15 text-warning',
    danger: 'bg-danger/10 text-danger',
  };

  const content = (
    <>
      <div className="flex items-start justify-between gap-3">
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}
        >
          {icon}
        </span>

        {href && (
          <ArrowRight className="h-4 w-4 text-bodydark2 transition group-hover:translate-x-0.5 group-hover:text-primary" />
        )}
      </div>

      <p className="mt-4 text-xs font-medium uppercase tracking-wide text-bodydark2">
        {label}
      </p>

      <p className="mt-1 text-2xl font-bold text-black dark:text-white">
        {value}
      </p>

      <div className="mt-2 min-h-[20px] text-xs text-bodydark2">{detail}</div>
    </>
  );

  return href ? (
    <button
      type="button"
      onClick={() => navigate(href)}
      className="group rounded-xl border border-stroke bg-white p-4 text-left shadow-default transition hover:-translate-y-0.5 hover:border-primary dark:border-strokedark dark:bg-boxdark"
    >
      {content}
    </button>
  ) : (
    <article className="rounded-xl border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark">
      {content}
    </article>
  );
};

export const SalesChange = ({ value }: { value: number }) => {
  const Icon = value > 0 ? ArrowUpRight : value < 0 ? ArrowDownRight : Minus;

  const className =
    value > 0 ? 'text-success' : value < 0 ? 'text-danger' : 'text-bodydark2';

  return (
    <span className={`inline-flex items-center gap-1 font-medium ${className}`}>
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(value).toLocaleString('es-SV', { maximumFractionDigits: 1 })}%
      vs. día anterior
    </span>
  );
};

export const SalesTrend = ({
  sales,
}: {
  sales: RestaurantDashboard['sales'];
}) => {
  const points = sales.lastSevenDays ?? [];

  const options: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      fontFamily: 'Satoshi, sans-serif',
      zoom: { enabled: false },
    },
    colors: ['#3C50E0'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        opacityFrom: 0.35,
        opacityTo: 0.03,
        stops: [0, 95, 100],
      },
    },
    grid: {
      borderColor: '#E2E8F0',
      strokeDashArray: 4,
    },
    xaxis: {
      categories: points.map((item) => formatDashboardDate(item.date)),
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        formatter: (value) => formatDashboardMoney(value),
      },
    },
    tooltip: {
      y: {
        formatter: (value) => formatDashboardMoney(value),
      },
    },
  };

  return (
    <DashboardPanel
      title="Ventas de los últimos siete días"
      subtitle="Solo cobros aceptados"
    >
      {points.length ? (
        <div className="px-2 pb-2 pt-4">
          <ReactApexChart
            options={options}
            series={[
              {
                name: 'Ventas',
                data: points.map((item) => item.total),
              },
            ]}
            type="area"
            height={310}
          />
        </div>
      ) : (
        <EmptyState
          icon={<ReceiptText className="h-7 w-7" />}
          text="Aún no hay ventas aceptadas para este período."
        />
      )}
    </DashboardPanel>
  );
};

export const EmptyState = ({
  icon,
  text,
}: {
  icon?: ReactNode;
  text: string;
}) => (
  <div className="flex min-h-[150px] flex-col items-center justify-center px-6 py-10 text-center text-sm text-bodydark2">
    <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-2 text-bodydark2 dark:bg-meta-4 dark:text-bodydark">
      {icon ?? <PackageOpen className="h-6 w-6" />}
    </span>
    <p>{text}</p>
  </div>
);

const orderLabels: Record<
  RestaurantDashboard['recentActiveOrders'][number]['status'],
  { label: string; classes: string }
> = {
  OPEN: { label: 'Abierta', classes: 'bg-primary/10 text-primary' },
  CONFIRMED: { label: 'Confirmada', classes: 'bg-success/10 text-success' },
  IN_KITCHEN: { label: 'En cocina', classes: 'bg-warning/15 text-warning' },
  READY: { label: 'Lista', classes: 'bg-success/10 text-success' },
  BILLING: { label: 'En cobro', classes: 'bg-meta-6/15 text-meta-6' },
};

export const RecentActiveOrders = ({
  orders,
  branchId,
}: {
  orders: RestaurantDashboard['recentActiveOrders'];
  branchId: number;
}) => {
  const navigate = useNavigate();

  const goToOrders = () => {
    navigate(RESTAURANT_ROUTES.orders);
  };

  const goToOrder = (orderId: number) => {
    navigate(
      `${RESTAURANT_ROUTES.orders}?branchId=${branchId}&orderId=${orderId}`
    );
  };

  return (
    <DashboardPanel
      title="Órdenes activas recientes"
      subtitle="Seleccione una orden para continuarla"
      action={
        <button
          onClick={goToOrders}
          className="text-xs font-medium text-primary hover:underline"
        >
          Ver todas
        </button>
      }
    >
      {!orders.length ? (
        <EmptyState text="No hay órdenes activas en este momento." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead className="bg-gray-2 text-xs uppercase text-bodydark2 dark:bg-meta-4">
              <tr>
                <th className="px-5 py-3">Orden</th>
                <th className="px-5 py-3">Mesa</th>
                <th className="px-5 py-3">Estado</th>
                <th className="px-5 py-3">Abierta</th>
                <th className="px-5 py-3 text-right">Total</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-stroke dark:divide-strokedark">
              {orders.map((order) => {
                const status = orderLabels[order.status];

                return (
                  <tr
                    key={order.id}
                    tabIndex={0}
                    role="link"
                    onClick={() => goToOrder(order.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        goToOrder(order.id);
                      }
                    }}
                    className="cursor-pointer transition hover:bg-gray-2 focus:bg-gray-2 focus:outline-none dark:hover:bg-meta-4 dark:focus:bg-meta-4"
                  >
                    <td className="px-5 py-3.5 font-semibold text-black dark:text-white">
                      {order.orderNumber}
                    </td>

                    <td className="px-5 py-3.5">
                      {order.tableName || 'Sin mesa'}
                    </td>

                    <td className="px-5 py-3.5">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.classes}`}
                      >
                        {status.label}
                      </span>
                    </td>

                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock3 className="h-3.5 w-3.5" />
                        {formatDashboardTime(order.openedAt)}
                      </span>
                    </td>

                    <td className="px-5 py-3.5 text-right font-semibold text-black dark:text-white">
                      {formatDashboardMoney(order.total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardPanel>
  );
};
